import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Playlist, {
  addItem,
  addItems,
  insertTrack,
  moveItem,
  nextItemIndex,
  prevItemIndex,
  removeItems,
} from '../../models/playlist/playlist';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import Source from '../../models/source';
import NullTrack from '../../models/track/nullTrack';
import Constants from '../../settings';
import { loadPlaylist, setLsItem } from '../storage/localStorageService';
import { Context } from '../_globalContext/context';

/**
 * The queue as the last session left it.
 *
 * Two keys, because for years it was written under one and read back under
 * the other - `ActivePlaylistId` ('activeplaylist') going out,
 * `ActivePlaylistKey` ('activePlaylist') coming in - so nothing ever
 * survived a reload and nobody noticed, there being no other symptom. The
 * write is fixed; the old key is still read once so that whatever is sitting
 * in a browser right now is picked up rather than dropped.
 */
const loadActivePlaylist = function () {
  return loadPlaylist(Constants.ActivePlaylistKey) || loadPlaylist(Constants.ActivePlaylistId);
};
const persistActivePlaylistWrapper = (fn: (pl: Playlist) => void) => (pl: Playlist) => {
  fn(pl);
  setLsItem(Constants.ActivePlaylistKey, pl);
};

/**
 * Where the queue was started from, kept next to the queue and for as long.
 *
 * A bare string rather than JSON, and its own key rather than a field on
 * the stored queue: the queue's loader clears anything of an unexpected
 * shape, and a playlist id is not part of a playlist.
 */
const loadActivePlaylistSource = function () {
  return localStorage.getItem(Constants.ActivePlaylistSourceKey) || undefined;
};
const persistActivePlaylistSource =
  (fn: (id: string | undefined) => void) => (id: string | undefined) => {
    fn(id);

    if (id === undefined) {
      localStorage.removeItem(Constants.ActivePlaylistSourceKey);
    } else {
      localStorage.setItem(Constants.ActivePlaylistSourceKey, id);
    }
  };

/** The real play method */
const createActivePlaylist = function (
  setPlaylist: React.Dispatch<React.SetStateAction<Playlist>>,
  setCurPlaylistItem: React.Dispatch<React.SetStateAction<number>>,
  setCurTrack: React.Dispatch<React.SetStateAction<PlaylistItem>>,
  setSource: React.Dispatch<React.SetStateAction<string | undefined>>,
) {
  return function setActivePlaylist(
    tracks: PlaylistItem[],
    curPlItm: number = 0,
    play = false,
    /**
     * The library playlist these tracks were played from - a playlist's
     * id, `LibraryId`, or `ActivePlaylistId` when replayed from the queue
     * view. Left out when they came from anywhere else, and that is a
     * statement too: ctrl+L then goes to the queue, not to a playlist that
     * merely happens to hold the track.
     */
    sourcePlaylistId?: string,
  ) {
    const playlist = new Playlist(Constants.ActivePlaylistName, tracks, Constants.ActivePlaylistId);
    persistActivePlaylistWrapper(setPlaylist)(playlist);
    persistActivePlaylistSource(setSource)(sourcePlaylistId);

    setCurPlaylistItem(curPlItm);

    if (play && playlist.tracks[curPlItm]) {
      setCurTrack(playlist.tracks[curPlItm]);
    }
  };
};

/**
 * ctrl+L, on its way from the player to the playlist view.
 *
 * The player works out where to go (playlistToShow) and asks; the library
 * page switches to that playlist, and the playlist view showing it selects
 * the row, scrolls to it, and completes the request. State rather than an
 * event because the view that has to act may not be mounted yet - the
 * shortcut works from the YouTube page too - and it picks the request up
 * when it is.
 */
export interface LocateRequest {
  /** Fresh per request, so asking for the same place twice is two requests. */
  id: number;
  playlistId: string;
  /**
   * The queue entry that is playing - the object, so that a playlist which
   * holds the same track twice lands on the entry that is playing when it
   * can tell (see indexOfPlaylistItem).
   */
  item: PlaylistItem;
}

export interface IActivePlaylistContext {
  value: Playlist;
  setActivePlaylist: ReturnType<typeof createActivePlaylist>;
  /**
   * Allows the current AP to be modified 'in place', keeping current track.
   * @param tracks
   */
  updateActivePlaylist(tracks: PlaylistItem[]): void;
  /**
   * The idea is that we can have a queued PlaylistItem not currently playing
   * Maybe we do away with currentTrack, stop player autoplay and queue up current playlistItem
   * then call play on the player if applicable?
   */
  currentPlaylistIndex: number;
  setCurrentPlaylistItem: React.Dispatch<React.SetStateAction<number>>;

  addItem(playlistItem: PlaylistItem): void;
  addItems(playlistItems: PlaylistItem[]): void;
  insertTrack: ReturnType<typeof insertTrack>;
  removeItems: ReturnType<typeof removeItems>;
  clearPlaylist(): void;
  moveItem: ReturnType<typeof moveItem>;

  prev(): void;
  play(): void;
  next(): void;

  currentTrack: PlaylistItem;
  /**
   * Only used by youtube component
   */
  setCurrentTrack: React.Dispatch<React.SetStateAction<PlaylistItem>>;

  /**
   * The library playlist the queue was last started from, if it was one.
   * See setActivePlaylist's parameter of the same name.
   */
  sourcePlaylistId: string | undefined;
  /** ctrl+L's outstanding request, if any. See LocateRequest. */
  locateRequest: LocateRequest | undefined;
  /** Asks for the current track to be shown in this playlist. */
  requestLocate(playlistId: string): void;
  /** The view that showed it - or found nothing to show - says so. */
  completeLocate(id: number): void;
}

export const ActivePlaylistContext = React.createContext({} as IActivePlaylistContext);

interface Props {
  children: React.ReactNode;
}
export default function ActivePlaylist({ children }: Props) {
  console.debug('Rendering ActivePlaylist');
  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const [playlist, setPlaylist] = useState<Playlist>(() => Playlist.activeFrom([]));
  const [currentPlaylistItem, setCurrentPlaylistItem] = useState(-1);
  const [currentTrack, setCurrentTrack] = useState<PlaylistItem>(
    PlaylistItem.from(new NullTrack()),
  );

  const [sourcePlaylistId, setSourcePlaylistId] = useState<string>();
  const [locateRequest, setLocateRequest] = useState<LocateRequest>();
  const locateCount = useRef(0);

  useEffect(function localStorageLoad() {
    const stored = loadActivePlaylist();
    if (stored) {
      setPlaylist(stored);
    }
    setSourcePlaylistId(loadActivePlaylistSource());
  }, []);

  const requestLocate = useCallback(
    function requestLocate(playlistId: string) {
      locateCount.current += 1;

      setLocateRequest({
        id: locateCount.current,
        playlistId,
        item: currentTrack,
      });
    },
    [currentTrack],
  );

  const completeLocate = useCallback(function completeLocate(id: number) {
    // Only its own. A newer request is somebody else's to complete.
    setLocateRequest((current) => (current && current.id === id ? undefined : current));
  }, []);

  const updateActivePlaylist = useCallback(
    function (tracks: PlaylistItem[]) {
      const playlist = new Playlist(
        Constants.ActivePlaylistName,
        tracks,
        Constants.ActivePlaylistId,
      );
      persistActivePlaylistWrapper(setPlaylist)(playlist);

      const curPlItm = currentTrack as PlaylistItem;
      if (curPlItm.trackType !== undefined && curPlItm.trackType !== Source.Null) {
        setCurrentPlaylistItem(tracks.findIndex((x) => x === curPlItm));
      }
    },
    [currentTrack],
  );

  const setActivePlaylist = createActivePlaylist(
    setPlaylist,
    setCurrentPlaylistItem,
    setCurrentTrack,
    setSourcePlaylistId,
  );
  const ctxValue = useMemo(
    () => ({
      value: playlist,
      currentTrack,
      setCurrentTrack,
      currentPlaylistIndex: currentPlaylistItem,
      setCurrentPlaylistItem,

      sourcePlaylistId,
      locateRequest,
      requestLocate,
      completeLocate,

      setActivePlaylist,
      updateActivePlaylist,
      addItem: addItem(playlist, persistActivePlaylistWrapper(setPlaylist)),
      addItems: addItems(playlist, persistActivePlaylistWrapper(setPlaylist)),
      insertTrack: insertTrack(playlist, persistActivePlaylistWrapper(setPlaylist)),
      removeItems: removeItems(playlist, persistActivePlaylistWrapper(setPlaylist)),
      clearPlaylist: () => setPlaylist(Playlist.activeFrom([])),
      moveItem: moveItem(playlist, persistActivePlaylistWrapper(setPlaylist)),

      prev: () => {
        const idx = prevItemIndex(playlist, currentPlaylistItem);
        const item = idx === undefined ? undefined : playlist.tracks[idx];

        if (idx !== undefined && item !== undefined) {
          setCurrentPlaylistItem(idx);
          setCurrentTrack(item);
        }
      },
      play: () => {
        const item = playlist.tracks[currentPlaylistItem];

        if (item !== undefined) {
          setCurrentTrack(item);
        }
      },
      next: () => {
        const idx = nextItemIndex(
          playlist,
          currentPlaylistItem,
          ctxRef.current.settings.repeatState,
        );
        const nextTrack = idx === undefined ? undefined : playlist.tracks[idx];

        if (idx === undefined || nextTrack === undefined) {
          return;
        }

        setCurrentPlaylistItem(idx);
        // If repeating the same track we have to refresh the react state
        // so a change is detected
        if (nextTrack === currentTrack) {
          setCurrentTrack(PlaylistItem.from(new NullTrack()));
        }
        setCurrentTrack(nextTrack);
      },
    }),
    [
      playlist,
      currentTrack,
      currentPlaylistItem,
      sourcePlaylistId,
      locateRequest,
      requestLocate,
      completeLocate,
      setActivePlaylist,
      updateActivePlaylist,
    ],
  );

  return (
    <ActivePlaylistContext.Provider value={ctxValue}>{children}</ActivePlaylistContext.Provider>
  );
}
